import { Check, ChevronDown } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { UserAvatar } from '@/shared/components/common'
import { Popover } from '@/shared/components/ui'
import { useAssignableUsers } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { UserSummary } from '@/shared/types/domain'

export interface AssigneeCellProps {
  user: UserSummary | null
  /** El objeto viaja junto al id: la fila se repinta con el nombre sin esperar al server. */
  onChange: (userId: string | null, user: UserSummary | null) => void
  /**
   * Acota las opciones a la empresa de ese proyecto: ofrecer a alguien sin
   * acceso a esa empresa es ofrecer algo que el servidor rechaza.
   */
  projectId: string
  /** Etiqueta accesible: la fila densa no tiene label visible. */
  label?: string
  withName?: boolean
  align?: 'start' | 'end'
  className?: string
}

/**
 * El responsable, editable DESDE LA FILA.
 *
 * Mismo criterio que `DueDateCell`: lo que se ve sigue siendo el avatar, y el
 * selector aparece sólo cuando alguien lo pide. **No es un `AssigneePicker` por
 * fila**: cada uno pide `GET /users/assignable` de su proyecto al montarse, y
 * en un portafolio de doscientos proyectos eso son doscientos pedidos para
 * mostrar avatares. Acá la lista se pide recién al abrir, y queda cacheada.
 */
export function AssigneeCell({
  user,
  onChange,
  projectId,
  label,
  withName = false,
  align = 'start',
  className,
}: AssigneeCellProps) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const boton = useRef<HTMLButtonElement>(null)
  const cerrar = useCallback(() => setOpen(false), [])
  const etiqueta = label ?? t('common.label.owner')

  return (
    <div className={cn('min-w-0', className)}>
      <button
        ref={boton}
        type="button"
        onClick={() => setOpen((abierto) => !abierto)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${etiqueta}: ${user?.display_name ?? t('common.label.noOwner')}`}
        title={etiqueta}
        className={cn(
          'group flex max-w-full min-w-0 items-center gap-1 rounded-[var(--radius-control)] px-1 py-0.5',
          'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
        )}
      >
        <UserAvatar user={user} withName={withName} />
        {/* Como el chevron de la fase: la única señal de que esto se toca. */}
        <ChevronDown
          size={12}
          aria-hidden="true"
          className="shrink-0 text-text-3 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      </button>

      <Popover anchor={boton} open={open} onClose={cerrar} align={align} className="w-[200px] p-1">
        <AssigneeOptions
          projectId={projectId}
          selectedId={user?.id ?? null}
          label={etiqueta}
          onPick={(elegido) => {
            setOpen(false)
            if ((elegido?.id ?? null) !== (user?.id ?? null)) onChange(elegido?.id ?? null, elegido)
          }}
        />
      </Popover>
    </div>
  )
}

/** Montado sólo con el panel abierto: es lo que difiere el pedido de usuarios. */
function AssigneeOptions({
  projectId,
  selectedId,
  label,
  onPick,
}: {
  projectId: string
  selectedId: string | null
  label: string
  onPick: (user: UserSummary | null) => void
}) {
  const { data: users = [], isPending } = useAssignableUsers(projectId)
  const opciones: (UserSummary | null)[] = [null, ...users]

  return (
    <ul role="listbox" aria-label={label} aria-busy={isPending} className="flex flex-col">
      {opciones.map((opcion) => {
        const elegida = (opcion?.id ?? null) === selectedId
        return (
          <li key={opcion?.id ?? 'nadie'}>
            <button
              type="button"
              role="option"
              aria-selected={elegida}
              onClick={() => onPick(opcion)}
              className={cn(
                'flex w-full items-center gap-2 rounded-[var(--radius-control)] px-1.5 py-1 text-left text-xs',
                'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
              )}
            >
              <UserAvatar user={opcion} withName className="min-w-0 flex-1 truncate" />
              {elegida ? (
                <Check size={12} aria-hidden="true" className="shrink-0 text-signal" />
              ) : null}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
